import { useEffect, useState } from 'react';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [messageCount, setMessageCount] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger loading animation
    setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('translate-y-20', 'opacity-0');
          entry.target.classList.add('translate-y-0', 'opacity-100');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));

    const skillProgressBars = document.querySelectorAll('.skill-progress');
    const skillObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const progress = entry.target.getAttribute('data-progress');
          (entry.target as HTMLElement).style.width = `${progress}%`;
        }
      });
    }, { threshold: 0.5 });

    skillProgressBars.forEach(bar => skillObserver.observe(bar));

    return () => {
      observer.disconnect();
      skillObserver.disconnect();
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitButton.innerHTML;
    submitButton.disabled = true;
    submitButton.innerHTML = '<span class="flex items-center justify-center gap-2"><i class="ri-loader-4-line animate-spin"></i>Sending...</span>';

    try {
      const response = await fetch('https://readdy.ai/api/public/form-post/cm7zzqxqy000112jm5aqx0aqx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        submitButton.innerHTML = '<span class="flex items-center justify-center gap-2"><i class="ri-check-line"></i>Message Sent!</span>';
        form.reset();
        const textarea = form.querySelector('textarea');
        if (textarea) {
          const charCount = form.querySelector('.text-xs.text-gray-400');
          if (charCount) charCount.textContent = '0/500 characters';
        }
        setTimeout(() => {
          submitButton.disabled = false;
          submitButton.innerHTML = originalText;
        }, 3000);
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      submitButton.innerHTML = '<span class="flex items-center justify-center gap-2"><i class="ri-error-warning-line"></i>Failed. Try again</span>';
      setTimeout(() => {
        submitButton.disabled = false;
        submitButton.innerHTML = originalText;
      }, 3000);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const count = e.target.value.length;
    setMessageCount(count);
    const charCount = e.target.closest('div')?.querySelector('.text-xs.text-gray-400');
    if (charCount) {
      charCount.textContent = `${count}/500 characters`;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/15 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl animate-float"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-gray-900/95 backdrop-blur-md border-b border-gray-800 shadow-lg shadow-purple-500/10' : 'bg-gray-900/95 backdrop-blur-md border-b border-gray-800 shadow-lg shadow-purple-500/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-500/30 rounded-full blur-md group-hover:blur-lg transition-all duration-500"></div>
                <img 
                  src="https://static.readdy.ai/image/1194d1c81328ba85ff2a2f9d043e8d78/18f4812bfad8fc051573a9206620efd5.png" 
                  alt="Logo" 
                  className="relative h-10 w-10 object-contain transition-all duration-500 group-hover:scale-110 group-hover:rotate-12"
                />
              </div>
              <span className="transition-all duration-300 group-hover:text-purple-400 group-hover:tracking-wider">Mohammed Ali</span>
            </h1>

            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium transition-all duration-300 capitalize whitespace-nowrap cursor-pointer text-white/80 hover:text-white hover:scale-110 relative group">
                home
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium transition-all duration-300 capitalize whitespace-nowrap cursor-pointer text-white/80 hover:text-white hover:scale-110 relative group">
                about
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('projects')} className="text-sm font-medium transition-all duration-300 capitalize whitespace-nowrap cursor-pointer text-white/80 hover:text-white hover:scale-110 relative group">
                projects
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium transition-all duration-300 capitalize whitespace-nowrap cursor-pointer text-purple-400 hover:scale-110 relative group">
                contact
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
            </div>

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-white cursor-pointer hover:scale-110 transition-transform duration-300 z-50">
              <i className={`text-2xl transition-all duration-300 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden absolute top-full left-0 right-0 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800 transition-all duration-500 overflow-hidden ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 py-6 space-y-4">
            <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-3 text-white/80 hover:text-white hover:bg-purple-500/20 rounded-lg transition-all duration-300 capitalize font-medium border border-transparent hover:border-purple-500/30 hover:translate-x-2">
              <span className="flex items-center gap-3">
                <i className="ri-home-line text-purple-400"></i>
                home
              </span>
            </button>
            <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-3 text-white/80 hover:text-white hover:bg-purple-500/20 rounded-lg transition-all duration-300 capitalize font-medium border border-transparent hover:border-purple-500/30 hover:translate-x-2">
              <span className="flex items-center gap-3">
                <i className="ri-user-line text-purple-400"></i>
                about
              </span>
            </button>
            <button onClick={() => scrollToSection('projects')} className="block w-full text-left px-4 py-3 text-white/80 hover:text-white hover:bg-purple-500/20 rounded-lg transition-all duration-300 capitalize font-medium border border-transparent hover:border-purple-500/30 hover:translate-x-2">
              <span className="flex items-center gap-3">
                <i className="ri-briefcase-line text-purple-400"></i>
                projects
              </span>
            </button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-3 text-white/80 hover:text-white hover:bg-purple-500/20 rounded-lg transition-all duration-300 capitalize font-medium border border-transparent hover:border-purple-500/30 hover:translate-x-2">
              <span className="flex items-center gap-3">
                <i className="ri-mail-line text-purple-400"></i>
                contact
              </span>
            </button>
            <div className="pt-4 border-t border-gray-800">
              <p className="text-gray-400 text-sm mb-3 px-4">Connect with me</p>
              <div className="flex gap-3 justify-center">
                <a href="https://github.com/MohammedRostom" target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110">
                  <i className="ri-github-fill text-lg text-white"></i>
                </a>
                <a href="https://www.behance.net/mohamed255" target="_blank" rel="noopener noreferrer" aria-label="Behance" className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110">
                  <i className="ri-behance-fill text-lg text-white"></i>
                </a>
                <a href="https://www.linkedin.com/in/mhmed-rustom-b85607214/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110">
                  <i className="ri-linkedin-fill text-lg text-white"></i>
                </a>
                <a href="https://wa.me/201121022496" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110">
                  <i className="ri-whatsapp-fill text-lg text-white"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className={`relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-purple-900/20 to-black transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => (
              <div 
                key={i}
                className="absolute w-1 h-1 bg-purple-500/30 rounded-full animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${5 + Math.random() * 10}s`
                }}
              ></div>
            ))}
          </div>
        </div>

        <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center w-full transition-all duration-1000 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
          <div className="mb-8 mt-16 sm:mt-20 flex justify-center">
            <div className="relative w-48 h-48 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-purple-500 shadow-2xl shadow-purple-500/50 animate-float-slow hover:scale-110 transition-transform duration-500">
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/30 to-blue-500/30 animate-pulse"></div>
              <img 
                src="https://pps.services.adobe.com/api/profile/C2DC58DC6011253B0A495FCB@AdobeID/image/ff5aa502-0396-489d-83a8-25d7e1e6a4bc/230" 
                alt="Mohammed Ali - Flutter Developer and UI/UX Designer"
                title="Mohammed Ali - Flutter Developer and UI/UX Designer"
                className="relative w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 to-transparent"></div>
            </div>
          </div>

          <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 px-4 animate-text-shimmer">
            Hi, I'm <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 animate-gradient">Mohammed Ali</span>
          </h2>

          <p className="text-2xl sm:text-2xl md:text-3xl text-gray-300 font-bold mb-4 px-4 animate-fade-in-delayed">
            UI/UX Designer || Flutter Developer
          </p>

          <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed px-4 animate-fade-in-delayed-2">
            I craft seamless, responsive, and intuitive applications with visually captivating and user-centric interfaces across all devices
          </p>

          <div className="flex gap-4 justify-center items-center mb-12 px-4">
            <a href="https://github.com/MohammedRostom" target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110 hover:rotate-12 relative group">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <i className="ri-github-fill text-xl text-white relative z-10"></i>
            </a>
            <a href="https://www.behance.net/mohamed255" target="_blank" rel="noopener noreferrer" aria-label="Behance" className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110 hover:rotate-12 relative group">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <i className="ri-behance-fill text-xl text-white relative z-10"></i>
            </a>
            <a href="https://www.linkedin.com/in/mhmed-rustom-b85607214/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110 hover:rotate-12 relative group">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <i className="ri-linkedin-fill text-xl text-white relative z-10"></i>
            </a>
            <a href="https://wa.me/201121022496" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-purple-600 hover:border-purple-500 transition-all duration-300 cursor-pointer hover:scale-110 hover:rotate-12 relative group">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <i className="ri-whatsapp-fill text-xl text-white relative z-10"></i>
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-20 px-4">
            <button onClick={() => scrollToSection('projects')} className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-full overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/50 hover:scale-110 whitespace-nowrap cursor-pointer w-full sm:w-auto animate-bounce-slow">
              <span className="relative z-10 flex items-center justify-center gap-2">
                View My Work
                <i className="ri-arrow-right-line text-xl transition-transform duration-300 group-hover:translate-x-2"></i>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </button>
            <a href="https://wa.me/qr/YHEUJE6FI5P5E1" target="_blank" rel="noopener noreferrer" className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/20 hover:border-purple-500 hover:bg-white/20 transition-all duration-500 hover:scale-110 whitespace-nowrap cursor-pointer w-full sm:w-auto text-center relative group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <span className="relative z-10 flex items-center justify-center gap-2">
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </span>
            </a>
          </div>

          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
            <i className="ri-arrow-down-line text-3xl text-gray-400"></i>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-24 bg-gray-900 animate-on-scroll transition-all duration-1000 ease-out relative translate-y-20 animate-fade-in-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 animate-slide-down">About Me</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-blue-600 mx-auto animate-expand"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="stagger-card group animate-slide-in">
              <div className="w-full h-full relative overflow-hidden rounded-2xl">
                <img 
                  src="https://avatars.githubusercontent.com/u/92746046?v=4" 
                  alt="Mohammed Ali - About"
                  title="Mohammed Ali - Flutter Developer and UI/UX Designer"
                  className="rounded-2xl shadow-2xl object-cover object-top w-full h-full transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>

            <div className="stagger-card space-y-4 animate-slide-in">
              <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 hover:text-purple-400 transition-colors duration-300">Crafting Digital Experiences</h4>
              
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 hover:text-white transition-colors duration-300">
                Hello! I'm Mohamed Ali, a graduate of the Faculty of Computer and Information Science at Assiut University, with a strong passion for creating intuitive and visually engaging digital experiences that solve real-world user problems.
              </p>

              <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 hover:text-white transition-colors duration-300">
                With a strong foundation in both design and development, I specialize in transforming complex ideas into simple, elegant interfaces. I believe that great design is invisible – it simply works and delights users at every touchpoint.
              </p>

              <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-6 hover:text-white transition-colors duration-300">
                My expertise spans UI/UX design and Flutter development, allowing me to craft seamless, responsive, and intuitive applications across all devices.
              </p>

              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 sm:p-4 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-500 hover:-translate-y-1 cursor-pointer group">
                  <p className="text-gray-400 text-xs sm:text-sm mb-1 group-hover:text-purple-400 transition-colors duration-300">Email</p>
                  <p className="text-white font-medium text-xs sm:text-sm break-all">mohammedrostom011@.....</p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 sm:p-4 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-500 hover:-translate-y-1 cursor-pointer group">
                  <p className="text-gray-400 text-xs sm:text-sm mb-1 group-hover:text-purple-400 transition-colors duration-300">Phone</p>
                  <p className="text-white font-medium text-xs sm:text-sm break-all">+201121022496</p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 sm:p-4 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-500 hover:-translate-y-1 cursor-pointer group">
                  <p className="text-gray-400 text-xs sm:text-sm mb-1 group-hover:text-purple-400 transition-colors duration-300">Location</p>
                  <p className="text-white font-medium text-xs sm:text-sm break-all">Egypt (Assiut)</p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 sm:p-4 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-500 hover:-translate-y-1 cursor-pointer group">
                  <p className="text-gray-400 text-xs sm:text-sm mb-1 group-hover:text-purple-400 transition-colors duration-300">Age</p>
                  <p className="text-white font-medium text-xs sm:text-sm break-all">24</p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 sm:p-4 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-500 hover:-translate-y-1 cursor-pointer group">
                  <p className="text-gray-400 text-xs sm:text-sm mb-1 group-hover:text-purple-400 transition-colors duration-300">Degree</p>
                  <p className="text-white font-medium text-xs sm:text-sm break-all">Bachelor's degree</p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 sm:p-4 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-500 hover:-translate-y-1 cursor-pointer group">
                  <p className="text-gray-400 text-xs sm:text-sm mb-1 group-hover:text-purple-400 transition-colors duration-300">Freelance</p>
                  <p className="text-white font-medium text-xs sm:text-sm break-all">Available</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://drive.google.com/file/d/1afP9IiSn_Y4A6C9OBYEvVCl6yGm7zNnx/view" target="_blank" rel="noopener noreferrer" className="px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg font-medium transition-all duration-500 hover:shadow-lg hover:shadow-purple-500/50 hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 text-sm sm:text-base group">
                  <i className="ri-download-line group-hover:animate-bounce"></i>
                  UI/UX CV
                </a>
                <a href="https://drive.google.com/file/d/1H0qqsNFGDFVvgcQy9cSPG7R274tmcPUU/view?pli=1" target="_blank" rel="noopener noreferrer" className="px-4 sm:px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg font-medium border border-white/20 hover:bg-white/20 hover:border-purple-500 transition-all duration-500 hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 text-sm sm:text-base group">
                  <i className="ri-download-line group-hover:animate-bounce"></i>
                  Flutter CV
                </a>
              </div>
            </div>
          </div>

          {/* Technical Skills */}
          <div className="mb-12 sm:mb-16 animate-on-scroll translate-y-20 animate-fade-in-up">
            <h4 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8 text-center">Technical Skills</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { name: 'UI Design', progress: 95, icon: 'ri-palette-line', gradient: 'from-blue-500 to-cyan-500' },
                { name: 'UX Research', progress: 90, icon: 'ri-search-line', gradient: 'from-purple-500 to-pink-500' },
                { name: 'Wireframing', progress: 92, icon: 'ri-layout-line', gradient: 'from-indigo-500 to-blue-500' },
                { name: 'Prototyping', progress: 88, icon: 'ri-smartphone-line', gradient: 'from-green-500 to-emerald-500' },
                { name: 'Flutter', progress: 85, icon: 'ri-flutter-fill', gradient: 'from-blue-400 to-cyan-400' },
                { name: 'Development', progress: 87, icon: 'ri-code-s-slash-line', gradient: 'from-orange-500 to-red-500' }
              ].map((skill, index) => (
                <div key={index} className="skill-card group bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-gray-700 hover:border-purple-500">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${skill.gradient} flex items-center justify-center text-white text-2xl shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 flex-shrink-0`}>
                      <i className={skill.icon}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-gray-200 text-sm group-hover:text-purple-400 transition-colors duration-300">{skill.name}</p>
                        <span className="text-xs text-gray-400 font-medium">{skill.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`skill-progress h-full bg-gradient-to-r ${skill.gradient} rounded-full transition-all duration-1000 ease-out`}
                          data-progress={skill.progress}
                          style={{ width: '0%' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Soft Skills */}
          <div>
            <h4 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8 text-center">Soft Skills</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { name: 'Communication', icon: 'ri-chat-3-line' },
                { name: 'Problem-Solving', icon: 'ri-lightbulb-line' },
                { name: 'Collaboration', icon: 'ri-team-line' },
                { name: 'Critical Thinking', icon: 'ri-brain-line' }
              ].map((skill, index) => (
                <div key={index} className="stagger-card bg-gray-800 border border-gray-700 rounded-xl p-4 sm:p-6 text-center hover:shadow-lg hover:shadow-purple-500/20 hover:border-purple-500 transition-all duration-500 hover:-translate-y-2 cursor-pointer group animate-slide-in">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 flex items-center justify-center">
                    <i className={`${skill.icon} text-3xl sm:text-4xl text-purple-400 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500`}></i>
                  </div>
                  <p className="font-semibold text-gray-200 text-xs sm:text-base group-hover:text-purple-400 transition-colors duration-300">{skill.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 sm:py-24 bg-gray-800 animate-on-scroll translate-y-20 transition-all duration-1000 ease-out animate-fade-in-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Featured Projects</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-blue-600 mx-auto mb-6 animate-expand"></div>
            <p className="text-base sm:text-xl text-gray-300 max-w-2xl mx-auto px-4">
              Explore my latest work and see how I solve real-world problems through design and development
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
            {['All', 'UI/UX', 'Flutter Apps'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-all duration-500 whitespace-nowrap cursor-pointer text-sm sm:text-base ${
                  activeFilter === filter
                    ? 'bg-gradient-to-r from-purple-500 to-blue-600 text-white shadow-lg shadow-purple-500/50 scale-110'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600 hover:scale-105 hover:border-purple-500'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: 'Islamic App',
                description: 'Modern design focusing on usability and aesthetic appeal for enhanced user interaction.',
                image: 'https://mir-s3-cdn-cf.behance.net/project_modules/fs_webp/b9fa9e237207751.68fb9568c5857.png',
                tags: ['UI/UX', 'Design'],
                link: 'https://www.behance.net/gallery/237207751/Islmic-app-()',
                category: 'UI/UX'
              },
              {
                title: 'Landing page (ERP SaaS)',
                description: 'Modern design focusing on usability and aesthetic appeal for enhanced user interaction.',
                image: 'https://mir-s3-cdn-cf.behance.net/projects/max_808/baaec2238575793.Y3JvcCwyNTc0LDIwMTQsMTk3LDA.png',
                tags: ['UI/UX', 'Design'],
                link: 'https://www.behance.net/gallery/238575793/Cloud-ERP-Landing-page-(ERP-SaaS)',
                category: 'UI/UX'
              },
              {
                title: 'Elyaf Academy',
                description: 'Modern design focusing on usability and aesthetic appeal for enhanced user interaction.',
                image: 'https://mir-s3-cdn-cf.behance.net/projects/404/07f2d1217729425.Y3JvcCwyNzMwLDIxMzYsMTAxLDA.png',
                tags: ['UI/UX', 'Design'],
                link: 'https://www.behance.net/gallery/217729425/UI-Case-study-(elyaf-academy)',
                category: 'UI/UX'
              },
              {
                title: 'Vodafone Redesign',
                description: 'Modern design focusing on usability and aesthetic appeal for enhanced user interaction.',
                image: 'https://mir-s3-cdn-cf.behance.net/projects/max_808/b7752f215470989.Y3JvcCwyNjA1LDIwMzgsMTYxLDA.png',
                tags: ['UI/UX', 'Design'],
                link: 'https://www.behance.net/gallery/215470989/Vodafone-redesign',
                category: 'UI/UX'
              },
              {
                title: 'Prime Style',
                description: 'Modern design focusing on usability and aesthetic appeal for enhanced user interaction.',
                image: 'https://mir-s3-cdn-cf.behance.net/project_modules/fs_webp/616bfe222462957.67e62611945a2.png',
                tags: ['UI/UX', 'Design'],
                link: 'https://www.behance.net/gallery/222462957/Prime-Style-fashion-app-UIUX-case-study',
                category: 'UI/UX'
              },
              {
                title: 'Real Estate',
                description: 'Modern design focusing on usability and aesthetic appeal for enhanced user interaction.',
                image: 'https://mir-s3-cdn-cf.behance.net/projects/808/593cc4213960757.Y3JvcCwyMTg2LDE3MDksMCww.png',
                tags: ['UI/UX', 'Design'],
                link: 'https://www.behance.net/gallery/213960757/UIUX-Casstudy-Real-estate-(Sakkny)',
                category: 'UI/UX'
              },
              {
                title: 'E_Book-Store',
                description: 'A comprehensive mobile application with intuitive user interface and seamless user experience.',
                image: 'https://cdn.shopaccino.com/igmguru/products/flutter-igmguru_1527424732_xl.jpg?v=532',
                tags: ['Flutter', 'Mobile'],
                link: 'https://github.com/MohammedRostom/E_Book-Store',
                category: 'Flutter Apps'
              },
              {
                title: 'Chat-Ai',
                description: 'A comprehensive mobile application with intuitive user interface and seamless user experience.',
                image: 'https://cdn.shopaccino.com/igmguru/products/flutter-igmguru_1527424732_xl.jpg?v=532',
                tags: ['Flutter', 'Mobile'],
                link: 'https://github.com/MohammedRostom/Chat-Ai',
                category: 'Flutter Apps'
              },
              {
                title: 'E_commerceApp-SOUQLY',
                description: 'A comprehensive mobile application with intuitive user interface and seamless user experience.',
                image: 'https://cdn.shopaccino.com/igmguru/products/flutter-igmguru_1527424732_xl.jpg?v=532',
                tags: ['Flutter', 'Mobile'],
                link: 'https://github.com/MohammedRostom/E_commerceApp-SOUQLY-',
                category: 'Flutter Apps'
              },
              {
                title: 'MusicApp',
                description: 'A comprehensive mobile application with intuitive user interface and seamless user experience.',
                image: 'https://cdn.shopaccino.com/igmguru/products/flutter-igmguru_1527424732_xl.jpg?v=532',
                tags: ['Flutter', 'Mobile'],
                link: 'https://github.com/MohammedRostom/MusicApp_Audio-Access_Storage',
                category: 'Flutter Apps'
              },
              {
                title: 'Face_detection',
                description: 'A comprehensive mobile application with intuitive user interface and seamless user experience.',
                image: 'https://cdn.shopaccino.com/igmguru/products/flutter-igmguru_1527424732_xl.jpg?v=532',
                tags: ['Flutter', 'Mobile'],
                link: 'https://github.com/MohammedRostom/Face_detection-Flutter-Ai-',
                category: 'Flutter Apps'
              },
              {
                title: 'QuranyApp',
                description: 'A comprehensive mobile application with intuitive user interface and seamless user experience.',
                image: 'https://cdn.shopaccino.com/igmguru/products/flutter-igmguru_1527424732_xl.jpg?v=532',
                tags: ['Flutter', 'Mobile'],
                link: 'https://github.com/MohammedRostom/QuranyApp',
                category: 'Flutter Apps'
              }
            ].filter(project => activeFilter === 'All' || project.category === activeFilter).map((project, index) => (
              <div key={index} className="stagger-card group bg-gray-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-purple-500/30 transition-all duration-700 hover:-translate-y-3 cursor-pointer border border-gray-700 hover:border-purple-500 animate-slide-in">
                <div className="relative overflow-hidden h-48 sm:h-64">
                  <img 
                    src={project.image}
                    alt={`${project.title} - ${project.category} project`}
                    title={`${project.title} - ${project.category}`}
                    className="w-full h-full object-cover object-top group-hover:scale-125 group-hover:rotate-2 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="w-16 h-16 rounded-full bg-purple-500/90 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-500">
                      <i className="ri-eye-line text-2xl text-white"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <h4 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3 group-hover:text-purple-400 transition-colors duration-300">{project.title}</h4>
                  <p className="text-sm sm:text-base text-gray-400 mb-3 sm:mb-4 line-clamp-2">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
                    {project.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full border transition-all duration-300 hover:scale-110 bg-purple-500/20 text-purple-400 border-purple-500/30 hover:bg-purple-500/30">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a href={project.link} target="_blank" rel="noopener noreferrer" className="group/btn flex items-center gap-2 text-purple-400 font-semibold hover:gap-4 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base">
                    View Project
                    <i className="ri-arrow-right-line text-lg sm:text-xl group-hover/btn:translate-x-2 transition-transform duration-300"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 bg-gray-900 animate-on-scroll transition-all duration-1000 ease-out relative translate-y-20 animate-fade-in-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Let's Work Together!</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-blue-600 mx-auto mb-6 animate-expand"></div>
            <p className="text-base sm:text-xl text-gray-300 max-w-2xl mx-auto px-4">
              Have a project in mind? I'd love to hear about it. Let's create something amazing together.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-start">
            <div className="stagger-card bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-700 hover:border-purple-500 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 animate-slide-in">
              <form className="space-y-4 sm:space-y-6" data-readdy-form onSubmit={handleSubmit} id="contact-form">
                <div className="group">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2 group-hover:text-purple-400 transition-colors duration-300">Your Name</label>
                  <input 
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 text-sm bg-gray-900 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 hover:border-purple-500"
                    placeholder="John Doe"
                  />
                </div>

                <div className="group">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2 group-hover:text-purple-400 transition-colors duration-300">Email Address</label>
                  <input 
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm bg-gray-900 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 hover:border-purple-500"
                    placeholder="john@example.com"
                  />
                </div>

                <div className="group">
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-300 mb-2 group-hover:text-purple-400 transition-colors duration-300">Subject</label>
                  <input 
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 text-sm bg-gray-900 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 hover:border-purple-500"
                    placeholder="Project Inquiry"
                  />
                </div>

                <div className="group">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-2 group-hover:text-purple-400 transition-colors duration-300">Message</label>
                  <textarea 
                    id="message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    onChange={handleTextareaChange}
                    className="w-full px-4 py-3 text-sm bg-gray-900 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 resize-none hover:border-purple-500"
                    placeholder="Tell me about your project..."
                  ></textarea>
                  <p className="text-xs text-gray-400 mt-1">0/500 characters</p>
                </div>

                <button 
                  type="submit"
                  className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-purple-500/50 transition-all duration-500 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm sm:text-base group"
                >
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"></i>
                    Send Message
                  </span>
                </button>
              </form>
            </div>

            <div className="space-y-6 sm:space-y-8">
              <div className="stagger-card bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-700 hover:border-purple-500 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 animate-slide-in">
                <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Get in Touch</h4>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4 group hover:translate-x-2 transition-transform duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <i className="ri-map-pin-line text-xl sm:text-2xl"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-white mb-1 text-sm sm:text-base group-hover:text-purple-400 transition-colors duration-300">Location</h5>
                      <p className="text-gray-300 text-sm sm:text-base">Egypt, Assiut</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 group hover:translate-x-2 transition-transform duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <i className="ri-mail-line text-xl sm:text-2xl"></i>
                    </div>
                    <div className="break-all">
                      <h5 className="font-semibold text-white mb-1 text-sm sm:text-base group-hover:text-purple-400 transition-colors duration-300">Email</h5>
                      <a href="mailto:mohammedrostom011@gmail.com" className="text-purple-400 hover:text-purple-300 transition-colors duration-300 cursor-pointer text-sm sm:text-base">mohammedrostom011@gmail.com</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 group hover:translate-x-2 transition-transform duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <i className="ri-phone-line text-xl sm:text-2xl"></i>
                    </div>
                    <div className="break-all">
                      <h5 className="font-semibold text-white mb-1 text-sm sm:text-base group-hover:text-purple-400 transition-colors duration-300">Phone</h5>
                      <a href="tel:+201121022496" className="text-purple-400 hover:text-purple-300 transition-colors duration-300 cursor-pointer text-sm sm:text-base">+201121022496</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 group hover:translate-x-2 transition-transform duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <i className="ri-whatsapp-line text-xl sm:text-2xl"></i>
                    </div>
                    <div className="break-all">
                      <h5 className="font-semibold text-white mb-1 text-sm sm:text-base group-hover:text-purple-400 transition-colors duration-300">WhatsApp</h5>
                      <a href="https://wa.me/201121022496" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors duration-300 cursor-pointer text-sm sm:text-base">Available</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stagger-card bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-6 sm:p-8 text-white shadow-xl hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-500 hover:scale-105 animate-slide-in">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <h4 className="text-lg sm:text-xl font-bold">Available for Projects</h4>
                </div>
                <p className="text-purple-100 leading-relaxed text-sm sm:text-base">
                  I'm currently accepting new projects and collaborations. Let's discuss how I can help bring your vision to life!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-gray-400 py-8 sm:py-12 px-4 sm:px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <h4 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 hover:text-purple-400 transition-colors duration-300 flex items-center gap-3">
                <img 
                  src="https://static.readdy.ai/image/1194d1c81328ba85ff2a2f9d043e8d78/18f4812bfad8fc051573a9206620efd5.png" 
                  alt="Logo" 
                  className="h-8 w-8 object-contain"
                />
                Mohammed Ali
              </h4>
              <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
                Flutter Developer & UI/UX Designer crafting beautiful and functional digital experiences.
              </p>
            </div>

            <div>
              <h5 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Quick Links</h5>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('home')} className="hover:text-purple-400 transition-all duration-300 cursor-pointer text-sm sm:text-base hover:translate-x-2 inline-block">Home</button></li>
                <li><button onClick={() => scrollToSection('about')} className="hover:text-purple-400 transition-all duration-300 cursor-pointer text-sm sm:text-base hover:translate-x-2 inline-block">About</button></li>
                <li><button onClick={() => scrollToSection('projects')} className="hover:text-purple-400 transition-all duration-300 cursor-pointer text-sm sm:text-base hover:translate-x-2 inline-block">Projects</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-purple-400 transition-all duration-300 cursor-pointer text-sm sm:text-base hover:translate-x-2 inline-block">Contact</button></li>
              </ul>
            </div>

            <div>
              <h5 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Connect</h5>
              <div className="flex gap-3 sm:gap-4">
                <a href="https://github.com/MohammedRostom" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all duration-500 cursor-pointer hover:scale-125 hover:rotate-12">
                  <i className="ri-github-fill text-lg sm:text-xl"></i>
                </a>
                <a href="https://www.behance.net/mohamed255" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all duration-500 cursor-pointer hover:scale-125 hover:rotate-12">
                  <i className="ri-behance-fill text-lg sm:text-xl"></i>
                </a>
                <a href="https://www.linkedin.com/in/mhmed-rustom-b85607214/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all duration-500 cursor-pointer hover:scale-125 hover:rotate-12">
                  <i className="ri-linkedin-fill text-lg sm:text-xl"></i>
                </a>
                <a href="https://wa.me/201121022496" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all duration-500 cursor-pointer hover:scale-125 hover:rotate-12">
                  <i className="ri-whatsapp-fill text-lg sm:text-xl"></i>
                </a>
                <a href="https://figma.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all duration-500 cursor-pointer hover:scale-125 hover:rotate-12">
                  <i className="ri-figma-fill text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4 text-center">
            <p className="text-xs sm:text-sm">© 2024 Mohammed Ali. All rights reserved.</p>
            <p className="text-xs sm:text-sm">Designed by Mohammed Rostom</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-purple-400 transition-colors duration-300 cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}